// src/components/molecules/ModernToast/ModernToast.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Fade, Slide, useTheme } from '@mui/material';

/**
 * ModernToast - Notificação estilo Instagram
 *
 * Toast minimalista full-width no rodapé.
 * Usa cores do tema ativo com contraste adequado.
 * Design simples e discreto, sem ícones.
 *
 * CLASSIFICAÇÃO: MOLÉCULA
 *
 * @example
 * const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
 *
 * <ModernToast
 *   open={toast.open}
 *   message={toast.message}
 *   severity={toast.severity}
 *   onClose={() => setToast({ ...toast, open: false })}
 * />
 */
const ModernToast = ({
    open,
    message,
    severity = 'success',
    onClose,
    duration = 2500,
    position = 'top-right'
}) => {
    const theme = useTheme();
    useEffect(() => {
        if (open) {
            // Auto-fechar após duração
            const closeTimer = setTimeout(() => {
                onClose();
            }, duration);

            return () => {
                clearTimeout(closeTimer);
            };
        }
    }, [open, duration, onClose]);

    // Posição do toast
    const positions = {
        'top-right': { top: 24, right: 24 },
        'top-center': { top: 24, left: '50%', transform: 'translateX(-50%)' },
        'top-left': { top: 24, left: 24 },
        'bottom-right': { bottom: 24, right: 24 },
        'bottom-center': { bottom: 0, left: 0, right: 0 }, // Full width no rodapé
        'bottom-left': { bottom: 24, left: 24 },
        'center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    };

    // Cores baseadas no severity
    const severityColors = {
        success: theme.palette.primary.main,
        error: theme.palette.error.main,
        warning: theme.palette.warning.main,
        info: theme.palette.info.main,
    };
    const bgColor = severityColors[severity] || severityColors.success;

    // Verificar se é full-width (bottom-center)
    const isFullWidth = position === 'bottom-center';

    // Para posição central, usar Fade ao invés de Slide
    const AnimationWrapper = position === 'center' ? Fade : Slide;
    const animationProps = position === 'center'
        ? {}
        : { direction: position === 'bottom-center' ? 'up' : position.includes('right') ? 'left' : position.includes('left') ? 'right' : 'down' };

    return (
        <AnimationWrapper
            {...animationProps}
            in={open}
            mountOnEnter
            unmountOnExit
        >
            <Box
                style={{
                    position: 'fixed',
                    ...positions[position],
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0px',
                    padding: isFullWidth ? '16px 24px' : '12px 24px',
                    borderRadius: isFullWidth ? '0' : '8px',
                    backgroundColor: bgColor,
                    boxShadow: isFullWidth ? '0 -2px 8px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.4)',
                    width: isFullWidth ? '100%' : 'auto',
                    minWidth: isFullWidth ? '100%' : 'auto',
                    minHeight: 'auto',
                    maxWidth: isFullWidth ? '100%' : '400px',
                }}
            >
                {/* Ícone removido para estilo minimalista */}

                {/* Mensagem */}
                <Typography
                    variant="body2"
                    style={{
                        color: theme.palette.getContrastText(bgColor),
                        fontWeight: 400,
                        fontSize: '0.9375rem',
                        textAlign: 'center',
                        lineHeight: 1.4,
                    }}
                >
                    {message}
                </Typography>
            </Box>
        </AnimationWrapper>
    );
};

ModernToast.propTypes = {
    /** Se o toast está visível */
    open: PropTypes.bool.isRequired,
    /** Mensagem a ser exibida */
    message: PropTypes.string.isRequired,
    /** Tipo da notificação */
    severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
    /** Função chamada ao fechar */
    onClose: PropTypes.func.isRequired,
    /** Duração em ms antes de fechar automaticamente */
    duration: PropTypes.number,
    /** Posição do toast na tela */
    position: PropTypes.oneOf([
        'top-right',
        'top-center',
        'top-left',
        'bottom-right',
        'bottom-center',
        'bottom-left',
        'center',
    ]),
};

export default ModernToast;
